import React, { useEffect, useMemo, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { Button, Loader, Tabs } from '@ok/okd';

import { toLocale } from '@common/i18n';

import history from '../../common/utils/history';

import {
  ALL_FILTER_ID,
  COLLAPSED_JOBS_DESKTOP,
  COLLAPSED_JOBS_MOBILE,
} from './consts';
import { useHeroVideoLoop, useIsMobile, useRecruitPositions } from './hooks';

import './top.less';

const Top = () => {
  const [activeTypeId, setActiveTypeId] = useState(ALL_FILTER_ID); // currently selected filter tab
  const [showAll, setShowAll] = useState(false); // whether the "show more" list is expanded
  const isMobile = useIsMobile();
  const heroVideoRef = useHeroVideoLoop();
  const { jobs, loading, availableTypes } = useRecruitPositions(activeTypeId);

  // Build locale/CDN values once from okGlobal
  const {
    isJobDescNoticeVisible,
    heroGridScanVideoUrl,
    allFilter,
    reasonItems,
    bottomLightBg,
  } = useMemo(() => {
    const { cdnBaseUrl = '', locale = 'ja_JP' } = window.okGlobal || {};
    const recruitImageUrl = (fileName) =>
      `${cdnBaseUrl}/cdn/assets/imgs/recruit/${fileName}`;
    return {
      // Job descriptions are JP-only; show a notice to non-ja_JP visitors
      isJobDescNoticeVisible: locale !== 'ja_JP',
      heroGridScanVideoUrl: recruitImageUrl('recruit-hero-grid-scan.mp4'),
      bottomLightBg: `url(${recruitImageUrl('recruit-bottom-glow.svg')})`,
      allFilter: { id: ALL_FILTER_ID, name: toLocale('recruit_filter_all') },
      reasonItems: [
        {
          title: toLocale('recruit_reason_flat_title'),
          description: toLocale('recruit_reason_flat_desc'),
          icon: recruitImageUrl('recruit-icon-thumb.svg'),
        },
        {
          title: toLocale('recruit_reason_global_title'),
          description: toLocale('recruit_reason_global_desc'),
          icon: recruitImageUrl('recruit-icon-globe.svg'),
        },
        {
          title: toLocale('recruit_reason_learn_title'),
          description: toLocale('recruit_reason_learn_desc'),
          icon: recruitImageUrl('recruit-icon-knowledge.svg'),
        },
        {
          title: toLocale('recruit_reason_speed_title'),
          description: toLocale('recruit_reason_speed_desc'),
          icon: recruitImageUrl('recruit-icon-speed.svg'),
        },
      ],
    };
  }, []);

  // Collapse the list when the filter or layout changes
  useEffect(() => {
    setShowAll(false);
  }, [activeTypeId, isMobile]);

  // Tabs = All + the non-empty position types resolved by the hook
  const availableFilters = [allFilter, ...availableTypes];

  // Collapsed shows fewer cards on mobile; "show more" reveals the full list
  const collapsedVisibleCount = isMobile
    ? COLLAPSED_JOBS_MOBILE
    : COLLAPSED_JOBS_DESKTOP;
  const visibleCount = showAll ? jobs.length : collapsedVisibleCount;
  const visibleJobs = jobs.slice(0, visibleCount);

  return (
    <div className="container">
      <div className="recruit-top">
        <section className="recruit-top__hero">
          <div className="recruit-top__hero-bg">
            <video
              ref={heroVideoRef}
              className="recruit-top__hero-video"
              src={heroGridScanVideoUrl}
              autoPlay
              muted
              playsInline
              preload="metadata"
              aria-hidden="true"
            />
          </div>
          <div className="recruit-top__hero-content">
            <h1 className="recruit-top__hero-title">
              <span>Build the </span>
              <span className="recruit-top__hero-title-highlight">Future</span>
              <br />
              <span>with Us</span>
            </h1>
            <p className="recruit-top__hero-desc">
              {toLocale('recruit_top_hero_desc')}
            </p>
          </div>
        </section>

        <section className="recruit-top__openings">
          <h2 className="recruit-top__section-title">
            {toLocale('recruit_top_openings_title')}
          </h2>
          <Tabs
            type={Tabs.TYPE.segmented}
            className="recruit-top__filters"
            activeId={activeTypeId}
            onChange={setActiveTypeId}
            hideContent
            hideNav
            aria-label={toLocale('recruit_top_filter_aria')}
          >
            {availableFilters.map((filter) => (
              <Tabs.TabPane key={filter.id} id={filter.id} tab={filter.name} />
            ))}
          </Tabs>
          {isJobDescNoticeVisible && (
            <p className="recruit-top__notice">
              {toLocale('recruit_top_jp_only_notice')}
            </p>
          )}
          {loading ? (
            <div className="recruit-top__jobs-loading">
              <Loader
                type={Loader.TYPE.primary}
                size={Loader.SIZE.md}
                shape={Loader.SHAPE.circle}
              />
            </div>
          ) : (
            <>
              <div className="recruit-top__jobs">
                {visibleJobs.map((job) => (
                  <article key={job.id} className="recruit-top__job-card">
                    <div className="recruit-top__job-body">
                      <span className="recruit-top__department-tag">
                        {job.departmentName}
                      </span>
                      <div className="recruit-top__job-text">
                        <h3 className="recruit-top__job-title">{job.name}</h3>
                      </div>
                      <div className="recruit-top__job-tags">
                        {job.tags?.map((tag, tagIdx) => (
                          <span key={tagIdx}>{tag}</span>
                        ))}
                      </div>
                    </div>
                    <Button
                      className="recruit-top__job-button"
                      onClick={() => history.push(`/recruit/job/${job.id}`)}
                    >
                      {toLocale('recruit_apply')}
                    </Button>
                  </article>
                ))}
              </div>
              {visibleCount < jobs.length && (
                <Button
                  className="recruit-top__more"
                  onClick={() => setShowAll(true)}
                >
                  {toLocale('recruit_top_more')}
                </Button>
              )}
            </>
          )}
        </section>

        <section className="recruit-top__reasons">
          <h2 className="recruit-top__section-title">
            {toLocale('recruit_top_reasons_title')}
          </h2>
          <div className="recruit-top__reason-list">
            {reasonItems.map((item) => (
              <div key={item.title} className="recruit-top__reason">
                <img
                  className="recruit-top__reason-icon"
                  src={item.icon}
                  alt=""
                  aria-hidden="true"
                />
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </section>

        <div
          className="recruit-top__bottom-light"
          style={{ '--bottom-light-bg': bottomLightBg }}
        />
      </div>
    </div>
  );
};

export default withRouter(Top);
